import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CreditCard, Copy, Check } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { paymentsService, type InitiatePaymentPayload } from '@/services/payments';
import { fmt } from '@/lib/format';
import type { SalesOrder } from '@/types/orders';
import type { InitiatedPaymentResponse, PaymentProvider } from '@/types/payments';

interface Props {
  order: SalesOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Take payment for an order. Two-stage UX:
 *
 *   Stage 1 — Form:
 *     Operator picks provider (default RAZORPAY) and confirms amount
 *     (defaults to the order's grandTotal — line subtotal + shipping +
 *     additional − discount + roundOff). Submitting calls
 *     `POST /payments/initiate`.
 *
 *   Stage 2 — Result:
 *     Surfaces the BE-returned `checkoutPayload`. For Razorpay this is the
 *     payload to feed the Razorpay JS SDK; for MANUAL provider flows we
 *     show the payment number + a copy-link helper so the operator can
 *     forward instructions to the customer.
 *
 * The webhook handler (`/webhooks/razorpay`) flips status from INITIATED
 * → SUCCESS asynchronously — operators see the order's payment status
 * update on the next list refresh (driven by query invalidation).
 */
export function PaymentInitiateDialog({ order, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [provider, setProvider] = useState<PaymentProvider>('RAZORPAY');
  const [amount, setAmount] = useState<string>('');
  const [externalRef, setExternalRef] = useState('');
  const [result, setResult] = useState<InitiatedPaymentResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProvider('RAZORPAY');
    // M-money-model — default to the tax-inclusive figure when the order's
    // tax preview is available (the same number the eventual invoice will
    // bill). Falls through to pre-tax grandTotal → legacy totalAmount.
    const withTax =
      order?.taxPreview?.available && order.taxPreview.customerPaysTotal != null
        ? Number(order.taxPreview.customerPaysTotal)
        : null;
    const def =
      withTax ??
      (order?.grandTotal != null
        ? Number(order.grandTotal)
        : order?.totalAmount != null
          ? Number(order.totalAmount)
          : null);
    setAmount(def != null ? String(def) : '');
    setExternalRef('');
    setResult(null);
    setCopied(false);
  }, [open, order]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: InitiatePaymentPayload = {
        salesOrderId: order!.id,
        provider,
        externalPaymentRef: externalRef.trim() || undefined,
        amount: amount ? Number(amount) : undefined,
        currency: order?.currency ?? 'INR',
      };
      return paymentsService.initiate(payload);
    },
    onSuccess: (res) => {
      toast.success(`Payment ${res.payment.paymentNumber} initiated`);
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['payments', 'forOrder', order?.id] });
      setResult(res);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not initiate payment'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!order) return;
    mutation.mutate();
  };

  const checkout = result?.checkoutPayload as
    | { keyId?: string; orderId?: string; amount?: number; checkoutUrl?: string }
    | undefined;

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isResultStage = !!result;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <CreditCard className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>
              {isResultStage ? 'Payment initiated' : 'Take payment'}
              {order && ` · ${order.orderNumber}`}
            </DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              {isResultStage
                ? `Share with ${order?.customer.name ?? 'customer'}`
                : (() => {
                    const withTax =
                      order?.taxPreview?.available && order.taxPreview.customerPaysTotal != null
                        ? Number(order.taxPreview.customerPaysTotal)
                        : null;
                    const def =
                      withTax ??
                      (order?.grandTotal != null
                        ? Number(order.grandTotal)
                        : order?.totalAmount != null
                          ? Number(order.totalAmount)
                          : null);
                    return `Collect ${def != null ? fmt.inr(def) : 'amount'}`;
                  })()}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed">
              {isResultStage
                ? 'Forward the checkout details to the customer. The order moves to PAID automatically once the gateway confirms.'
                : 'Initiates a payment through the chosen provider. The order auto-transitions to PAYMENT_PENDING.'}
            </DialogDescription>
          </div>
        </div>
      }
      title=""
      submitLabel={isResultStage ? 'Done' : 'Initiate payment'}
      onSubmit={
        isResultStage
          ? (e) => {
              e.preventDefault();
              onOpenChange(false);
            }
          : onSubmit
      }
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="2xl"
    >
      {!isResultStage ? (
        <>
          <FormField label="Provider" htmlFor="provider" required>
            <Select value={provider} onValueChange={(v) => setProvider(v as PaymentProvider)}>
              <SelectTrigger id="provider" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RAZORPAY">Razorpay</SelectItem>
                <SelectItem value="MANUAL">Manual (cash / bank transfer)</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Amount"
              htmlFor="amount"
              hint={
                order?.taxPreview?.available && order.taxPreview.customerPaysTotal != null
                  ? `Defaults to grand total with tax ${fmt.inr(Number(order.taxPreview.customerPaysTotal))}`
                  : order?.grandTotal != null
                    ? `Defaults to grand total ${fmt.inr(Number(order.grandTotal))}`
                    : order?.totalAmount != null
                      ? `Defaults to subtotal ${fmt.inr(Number(order.totalAmount))}`
                      : 'Order has no total — amount required'
              }
              required
            >
              <Input
                id="amount"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-9 text-right font-mono tabular-nums"
                required
              />
            </FormField>
            <FormField label="Currency" htmlFor="currency">
              <Input
                id="currency"
                value={order?.currency ?? 'INR'}
                disabled
                className="h-9 font-mono"
              />
            </FormField>
          </div>

          <FormField
            label="External reference"
            htmlFor="externalRef"
            hint="Optional — your portal's payment ID for reconciliation."
          >
            <Input
              id="externalRef"
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
              placeholder="pmt-abc-123"
              className="h-9"
            />
          </FormField>
        </>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-success/30 bg-success/5 p-4">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-success">
              Payment record
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 text-[12.5px]">
              <div>
                <div className="text-[10.5px] text-muted-foreground">Number</div>
                <div className="font-mono">{result.payment.paymentNumber}</div>
              </div>
              <div>
                <div className="text-[10.5px] text-muted-foreground">Status</div>
                <div className="font-mono">{result.payment.status}</div>
              </div>
              <div>
                <div className="text-[10.5px] text-muted-foreground">Amount</div>
                <div className="font-mono tabular-nums">
                  {fmt.inr(Number(result.payment.amount))}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] text-muted-foreground">Provider</div>
                <div className="font-mono">{result.payment.provider}</div>
              </div>
            </div>
          </div>

          {/* Razorpay path — surface gateway order id + checkout link */}
          {result.payment.provider === 'RAZORPAY' && checkout && (
            <div className="space-y-2 rounded-md border border-border/70 bg-card p-4">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Razorpay checkout
              </div>
              {checkout.orderId && (
                <CopyRow
                  label="Gateway order ID"
                  value={String(checkout.orderId)}
                  onCopy={copyText}
                  copied={copied}
                />
              )}
              {checkout.checkoutUrl && (
                <CopyRow
                  label="Checkout URL"
                  value={String(checkout.checkoutUrl)}
                  onCopy={copyText}
                  copied={copied}
                />
              )}
              <div className="pt-1 text-[11px] text-muted-foreground">
                Hand the URL to the customer or embed it in your portal. The Razorpay
                webhook will flip the order to PAID once captured.
              </div>
            </div>
          )}

          {/* Manual path — show payment number + nudge */}
          {result.payment.provider === 'MANUAL' && (
            <div className="space-y-2 rounded-md border border-info/20 bg-info/5 p-4 text-[12.5px]">
              <div className="font-medium">Manual settlement</div>
              <div className="text-muted-foreground">
                Once you've received the funds (cash, bank transfer, etc.), open this payment
                in the order's Payments tab and mark it{' '}
                <span className="font-mono">SUCCESS</span> to advance the order.
              </div>
            </div>
          )}
        </div>
      )}
    </FormSheet>
  );
}

function CopyRow({
  label,
  value,
  onCopy,
  copied,
}: {
  label: string;
  value: string;
  onCopy: (text: string) => void;
  copied: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] text-muted-foreground">{label}</div>
        <div className="truncate font-mono text-[12px]">{value}</div>
      </div>
      <Button type="button" size="sm" variant="outline" onClick={() => onCopy(value)}>
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
